import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Contractor, DashboardTaskItem, EstimateWithScopes, ShoppingListItem, TaskStatus } from '@bpd/shared';
import { formatCurrency, computeCoiStatus } from '@bpd/shared';
import { projectsApi } from '../../api/projects.ts';

type LoadState = 'loading' | 'ready' | 'error' | 'not-configured' | 'not-found';

const STATUS_LABEL: Record<TaskStatus, string> = {
  'not-started': 'Not started',
  'in-flight': 'In flight',
  complete: 'Complete',
};

const STATUS_BADGE_CLS: Record<TaskStatus, string> = {
  'not-started': 'bg-gray-100 text-gray-600',
  'in-flight': 'bg-phoenix-blue-100 text-phoenix-blue-700',
  complete: 'bg-green-100 text-green-800',
};

function Sep() {
  return (
    <span aria-hidden="true" className="text-gray-300">
      |
    </span>
  );
}

interface ClientGroup<T> {
  clientId: string;
  clientLabel: string;
  projects: {
    projectId: string;
    projectName: string;
    items: T[];
  }[];
}

type ClientProjectJoined = { clientId: string; clientLabel: string; projectId: string; projectName: string };

/** Groups a flat, already client/project-joined list (DashboardTaskItem[] or ShoppingListItem[])
 * into client -> project -> items, sorted alphabetically at both levels. `sortItems` decides the
 * order within a project — tasks sort by dashboardOrder (the same axis the cross-project Dashboard
 * uses, so ordering reads consistently between the two views); products have no such field, so the
 * Products section below sorts them by item name instead. */
function groupByClientAndProject<T extends ClientProjectJoined>(
  items: T[],
  sortItems: (a: T, b: T) => number,
): ClientGroup<T>[] {
  const byClient = new Map<string, { clientLabel: string; byProject: Map<string, { projectName: string; items: T[] }> }>();
  for (const item of items) {
    let client = byClient.get(item.clientId);
    if (!client) {
      client = { clientLabel: item.clientLabel, byProject: new Map() };
      byClient.set(item.clientId, client);
    }
    let project = client.byProject.get(item.projectId);
    if (!project) {
      project = { projectName: item.projectName, items: [] };
      client.byProject.set(item.projectId, project);
    }
    project.items.push(item);
  }
  return Array.from(byClient.entries())
    .map(([clientId, c]) => ({
      clientId,
      clientLabel: c.clientLabel,
      projects: Array.from(c.byProject.entries())
        .map(([projectId, p]) => ({
          projectId,
          projectName: p.projectName,
          items: p.items.sort(sortItems),
        }))
        .sort((a, b) => a.projectName.localeCompare(b.projectName)),
    }))
    .sort((a, b) => a.clientLabel.localeCompare(b.clientLabel));
}

const byDashboardOrder = (a: DashboardTaskItem, b: DashboardTaskItem) =>
  (a.dashboardOrder ?? 0) - (b.dashboardOrder ?? 0);
const byItemName = (a: ShoppingListItem, b: ShoppingListItem) => a.item.localeCompare(b.item);

/** A vendor's-eye view — every client/project a given contractor has tasks on, across the whole
 * account. A filtered subset of the cross-project Dashboard rather than a new data source: it reuses
 * listDashboardItems() (already joined with client/project context) and just narrows to one
 * contractorId, since a contractor commonly works multiple clients/projects at once. */
export function ContractorDetailPage() {
  const { contractorId } = useParams<{ contractorId: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>('loading');
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [items, setItems] = useState<DashboardTaskItem[]>([]);
  const [shoppingList, setShoppingList] = useState<ShoppingListItem[]>([]);
  const [estimates, setEstimates] = useState<EstimateWithScopes[]>([]);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);

  function load() {
    if (!contractorId) return;
    if (!projectsApi.isConfigured()) {
      setState('not-configured');
      return;
    }
    setState('loading');
    Promise.all([
      projectsApi.listContractors(),
      projectsApi.listDashboardItems(),
      projectsApi.getShoppingList(),
      projectsApi.listEstimatesByContractor(contractorId),
    ])
      .then(([contractorList, dashboardItems, shoppingListItems, estimateList]) => {
        setContractors(contractorList);
        if (!contractorList.some((c) => c.id === contractorId)) {
          setState('not-found');
          return;
        }
        setItems(dashboardItems);
        setShoppingList(shoppingListItems);
        setEstimates(estimateList);
        setState('ready');
      })
      .catch(() => setState('error'));
  }

  useEffect(load, [contractorId]);

  async function handleViewQuote(id: string) {
    setViewingId(id);
    setViewError(null);
    try {
      const { url } = await projectsApi.getEstimateDownloadUrl(id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setViewError(e instanceof Error ? e.message : 'Failed to open file');
    } finally {
      setViewingId(null);
    }
  }

  const contractor = contractors.find((c) => c.id === contractorId) ?? null;
  const assigned = useMemo(() => items.filter((i) => i.contractorId === contractorId), [items, contractorId]);
  const grouped = useMemo(() => groupByClientAndProject(assigned, byDashboardOrder), [assigned]);
  const projectCount = grouped.reduce((sum, c) => sum + c.projects.length, 0);

  // "Not yet handled" — no task link at all (nothing to say it's done), or linked to a task that
  // isn't complete yet. Confirmed with the user: an unlinked product still needs to show up here,
  // since there's no way to know it's already been taken care of.
  const outstandingProducts = useMemo(
    () =>
      shoppingList.filter(
        (l) => l.contractorId === contractorId && (!l.taskItemId || l.taskStatus !== 'complete'),
      ),
    [shoppingList, contractorId],
  );
  const productsGrouped = useMemo(
    () => groupByClientAndProject(outstandingProducts, byItemName),
    [outstandingProducts],
  );

  // Every project this contractor touches by any connection (assigned task, an outstanding
  // product line, or a quote scope) - a task-only view would miss a contractor who's only ever
  // been quoted or purchased from on a given project, not yet assigned a task there.
  const allProjects = useMemo(() => {
    const byProjectId = new Map<string, ClientProjectJoined>();
    for (const t of assigned) byProjectId.set(t.projectId, t);
    for (const p of outstandingProducts) byProjectId.set(p.projectId, p);
    for (const e of estimates) {
      for (const s of e.scopes) byProjectId.set(s.projectId, s);
    }
    return Array.from(byProjectId.values()).sort(
      (a, b) => a.clientLabel.localeCompare(b.clientLabel) || a.projectName.localeCompare(b.projectName),
    );
  }, [assigned, outstandingProducts, estimates]);
  const allProjectsClientCount = useMemo(
    () => new Set(allProjects.map((p) => p.clientId)).size,
    [allProjects],
  );

  const coi = contractor ? computeCoiStatus(contractor.coiExpirationDate) : null;
  const coiBadgeCls: Record<'green' | 'yellow' | 'red', string> = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-800',
  };

  return (
    <main className="mx-auto max-w-5xl px-6 py-16">
      <Link to="/admin/contractors" className="text-sm text-phoenix-blue-600 hover:underline">
        ← All contractors
      </Link>

      {state === 'not-configured' && (
        <p className="mt-8 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          The project management backend isn&apos;t connected yet in this environment.
        </p>
      )}
      {state === 'error' && (
        <p className="mt-8 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Couldn&apos;t load this contractor&apos;s work. Try again in a moment.
        </p>
      )}
      {state === 'not-found' && (
        <p className="mt-8 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          That contractor doesn&apos;t exist.
        </p>
      )}
      {state === 'loading' && <p className="mt-8 text-sm text-gray-500">Loading…</p>}

      {state === 'ready' && contractor && (
        <>
          <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-serif text-3xl font-bold text-phoenix-blue-900">{contractor.name}</h1>
            </div>
            {contractors.length > 1 && (
              <select
                value={contractorId}
                onChange={(e) => navigate(`/admin/contractors/${e.target.value}`)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              >
                {contractors.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <h2 className="mt-8 font-serif text-2xl font-semibold text-phoenix-blue-900">Demographics</h2>
          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            {contractor.trade && <span className="text-gray-700">{contractor.trade}</span>}
            {contractor.trade && contractor.phone && <Sep />}
            {contractor.phone && <span className="text-gray-700">{contractor.phone}</span>}
            {coi && coi.status !== 'none' && (
              <>
                {(contractor.trade || contractor.phone) && <Sep />}
                <span title={coi.reason} className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${coiBadgeCls[coi.status]}`}>
                  CoI: {coi.reason}
                </span>
              </>
            )}
            {!contractor.trade && !contractor.phone && (!coi || coi.status === 'none') && (
              <span className="text-gray-400">No contact details on file.</span>
            )}
          </div>
          {contractor.notes && <p className="mt-2 text-sm text-gray-600">{contractor.notes}</p>}

          <h2 className="mt-10 font-serif text-2xl font-semibold text-phoenix-blue-900">Projects</h2>
          <p className="mt-1 text-sm text-gray-500">
            {allProjects.length === 0
              ? 'Not linked to any project yet.'
              : `${allProjects.length} project${allProjects.length === 1 ? '' : 's'} for ${allProjectsClientCount} client${allProjectsClientCount === 1 ? '' : 's'}.`}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {allProjects.map((p) => (
              <Link
                key={p.projectId}
                to={`/admin/projects/clients/${p.clientId}/${p.projectId}`}
                className="rounded-full border border-gray-200 bg-white px-3 py-1 text-sm text-gray-700 hover:border-phoenix-blue-300"
              >
                {p.projectName} <span className="text-gray-400">— {p.clientLabel}</span>
              </Link>
            ))}
          </div>

          <h2 className="mt-10 font-serif text-2xl font-semibold text-phoenix-blue-900">Tasks</h2>
          <p className="mt-1 text-sm text-gray-500">
            {assigned.length === 0
              ? 'Not currently assigned to any tasks.'
              : `${assigned.length} task${assigned.length === 1 ? '' : 's'} across ${projectCount} project${projectCount === 1 ? '' : 's'} for ${grouped.length} client${grouped.length === 1 ? '' : 's'}.`}
          </p>

          <div className="mt-4 space-y-8">
            {grouped.map((client) => (
              <div key={client.clientId}>
                <h2 className="font-serif text-xl font-semibold text-phoenix-blue-900">{client.clientLabel}</h2>
                <div className="mt-3 space-y-5">
                  {client.projects.map((project) => (
                    <div key={project.projectId}>
                      <Link
                        to={`/admin/projects/clients/${client.clientId}/${project.projectId}`}
                        className="text-sm font-medium text-phoenix-blue-700 hover:underline"
                      >
                        {project.projectName}
                      </Link>
                      <div className="mt-2 space-y-2">
                        {project.items.map((item) => (
                          <Link
                            key={item.id}
                            to={`/admin/projects/clients/${client.clientId}/${project.projectId}`}
                            className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-sm hover:border-phoenix-blue-300 hover:shadow-md"
                          >
                            {item.parentTaskLabel && (
                              <>
                                <span className="text-gray-500">under: {item.parentTaskLabel}</span>
                                <Sep />
                              </>
                            )}
                            <span className="font-semibold text-gray-900">{item.item}</span>
                            <Sep />
                            <span
                              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE_CLS[item.status]}`}
                            >
                              {STATUS_LABEL[item.status]}
                            </span>
                            {item.date && (
                              <>
                                <Sep />
                                <span className="text-gray-500">{item.date}</span>
                              </>
                            )}
                            {item.description && (
                              <>
                                <Sep />
                                <span className="text-gray-600">{item.description}</span>
                              </>
                            )}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {grouped.length === 0 && (
              <p className="text-sm text-gray-400">No tasks currently assigned to this vendor.</p>
            )}
          </div>

          <h2 className="mt-10 font-serif text-2xl font-semibold text-phoenix-blue-900">Quotes</h2>
          <p className="mt-1 text-sm text-gray-500">
            {estimates.length === 0
              ? 'No estimates from this contractor yet.'
              : `${estimates.length} estimate${estimates.length === 1 ? '' : 's'} from this contractor.`}
          </p>
          <div className="mt-4 space-y-2">
            {estimates.map((e) => (
              <div key={e.id} className="rounded-md bg-gray-50 px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-medium text-gray-900">{e.title}</span>
                  <span className="text-gray-600">
                    {formatCurrency(e.totalAccepted)} accepted of {formatCurrency(e.totalPrice)}
                  </span>
                  {e.attachmentFileName && (
                    <button
                      type="button"
                      onClick={() => void handleViewQuote(e.id)}
                      disabled={viewingId === e.id}
                      className="text-phoenix-blue-600 hover:underline"
                    >
                      {viewingId === e.id ? 'Opening…' : 'View quote'}
                    </button>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {e.scopes.length === 0 && (
                    <span className="text-xs text-gray-400">Not attached to a project yet</span>
                  )}
                  {e.scopes.map((s) => (
                    <Link
                      key={`${s.projectId}-${s.taskItemId ?? ''}`}
                      to={`/admin/projects/clients/${s.clientId}/${s.projectId}`}
                      className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600 hover:border-phoenix-blue-300"
                    >
                      {s.clientLabel} / {s.projectName}
                      {s.taskLabel ? ` (${s.taskLabel})` : ''}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
            {viewError && <p className="text-xs text-red-600">{viewError}</p>}
          </div>

          <h2 className="mt-10 font-serif text-2xl font-semibold text-phoenix-blue-900">Products</h2>
          <p className="mt-1 text-sm text-gray-500">
            Purchase lines from this vendor whose task isn&apos;t complete yet — or that aren&apos;t
            linked to a task at all.
          </p>
          <div className="mt-4 space-y-8">
            {productsGrouped.map((client) => (
              <div key={client.clientId}>
                <h3 className="font-serif text-xl font-semibold text-phoenix-blue-900">{client.clientLabel}</h3>
                <div className="mt-3 space-y-5">
                  {client.projects.map((project) => (
                    <div key={project.projectId}>
                      <Link
                        to={`/admin/projects/clients/${client.clientId}/${project.projectId}`}
                        className="text-sm font-medium text-phoenix-blue-700 hover:underline"
                      >
                        {project.projectName}
                      </Link>
                      <div className="mt-2 space-y-2">
                        {project.items.map((line) => (
                          <Link
                            key={line.id}
                            to={`/admin/projects/clients/${client.clientId}/${project.projectId}`}
                            className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-sm hover:border-phoenix-blue-300 hover:shadow-md"
                          >
                            <span className="font-semibold text-gray-900">{line.item}</span>
                            <Sep />
                            <span className="text-gray-500">Qty {line.quantity}</span>
                            {line.sku && (
                              <>
                                <Sep />
                                <span className="text-gray-500">SKU: {line.sku}</span>
                              </>
                            )}
                            <Sep />
                            <span className="text-gray-500">{formatCurrency(line.unitPrice)} / unit</span>
                            <Sep />
                            {line.taskLabel && line.taskStatus ? (
                              <span
                                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE_CLS[line.taskStatus]}`}
                              >
                                {line.taskLabel} — {STATUS_LABEL[line.taskStatus]}
                              </span>
                            ) : (
                              <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
                                Not linked to a task
                              </span>
                            )}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {productsGrouped.length === 0 && (
              <p className="text-sm text-gray-400">Nothing outstanding to order from this vendor.</p>
            )}
          </div>
        </>
      )}
    </main>
  );
}
